import { DatePipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, input, linkedSignal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TasksActionsService } from '@app/shared/services/actions/tasks-actions.service';
import { DeviceService } from '@app/shared/services/shared/device.service';
import { ToastService } from '@app/shared/services/shared/toast.service';
import { TaskDescriptionAcknowledgementsDataService } from '@app/shared/services/task-description-acknowledgements-data.service';
import { TaskParticipantsDataService } from '@app/shared/services/task-participants-data.service';
import { Task } from '@app/shared/types/models/task/task.model';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';

export interface TaskAcknowledgementParticipantRow {
  readonly rowId: string;
  readonly displayName: string;
  readonly acknowledgedAt: Date | undefined;
  readonly icon: string;
}

/**
 * Checkbox enabling the "acknowledgement required" flag on a task, together with
 * the participant acknowledgement status list.
 *
 * Delegates the flag mutation to {@link TasksActionsService.updateTaskAcknowledgementRequested}
 * and reads acknowledgements from {@link TaskDescriptionAcknowledgementsDataService}.
 */
@Component({
  selector: 'proffeo-task-acknowledgement-required-panel',
  templateUrl: './task-acknowledgement-required-panel.component.html',
  host: {
    class: 'block w-full'
  },
  imports: [MatIconModule, MatSlideToggleModule, MatExpansionModule, TranslatePipe, DatePipe]
})
export class TaskAcknowledgementRequiredPanelComponent {
  private readonly tasksActions = inject(TasksActionsService);
  private readonly acknowledgementsData = inject(TaskDescriptionAcknowledgementsDataService);
  private readonly taskParticipantsData = inject(TaskParticipantsDataService);
  private readonly translate = inject(TranslateService);
  private readonly toastService = inject(ToastService);
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isMobile = this.deviceService.isMobile;
  protected readonly isSubmitting = signal(false);
  protected readonly isAcknowledgementRequired = linkedSignal(() => this.task().acknowledgementRequested ?? false);

  protected readonly acknowledgements = computed(() => this.acknowledgementsData.getAcknowledgements(this.task().id)());
  protected readonly acknowledgedCount = computed(
    () =>
      this.acknowledgements().filter(
        acknowledgement => acknowledgement.acceptedAt !== undefined && acknowledgement.acceptedAt !== null
      ).length
  );

  protected readonly acknowledgementRows = computed((): TaskAcknowledgementParticipantRow[] => {
    const taskParticipants = this.taskParticipantsData.getTaskParticipants(this.task().id)() ?? [];
    const rows: TaskAcknowledgementParticipantRow[] = [];

    for (const acknowledgement of this.acknowledgements()) {
      const user = taskParticipants
        .map(participant => participant.projectParticipant()?.user())
        .find(user => user?.id === acknowledgement.userId);

      if (!user) {
        continue;
      }

      const displayName = user.fullName?.trim() || this.translate.instant('task-description-comments.unknown-user');
      rows.push({
        rowId: user.id,
        displayName,
        acknowledgedAt: acknowledgement?.acceptedAt,
        icon: acknowledgement?.acceptedAt !== undefined && acknowledgement?.acceptedAt !== null ? 'check' : 'close'
      });
    }

    return rows.sort((first, second) => first.displayName.localeCompare(second.displayName));
  });

  public readonly task = input.required<Task>();

  protected onToggle(checked: boolean): void {
    if (this.isSubmitting()) {
      return;
    }

    this.isAcknowledgementRequired.set(checked);
    this.isSubmitting.set(true);
    this.tasksActions
      .updateTaskAcknowledgementRequested(this.task(), checked)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe({
        error: () => {
          this.isAcknowledgementRequired.set(!checked);
          this.toastService.error(
            this.translate.instant('task-description-acknowledgement-panel.require-acknowledgement-toast-error')
          );
        }
      });
  }
}
